import React, { useState, useEffect,useMemo, memo, useCallback } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import axios from 'axios';
import { useDrop } from 'react-dnd';

import CellTemplate from '../../components/canvas/CellTemplate';
import RectangleShape from '../../components/shapes/Rectangle/RectangleShape';
import CircleShape from '../../components/shapes/Circle/CircleShape';
import ArcShape from '../../components/shapes/Arc/ArcShape';
import ArrowShape from '../../components/shapes/Arrow/ArrowShape';
import ImageShape from '../../components/shapes/Image/ImageShape';
import TextShape from '../../components/shapes/Text/TextShape';
import LineShape from '../../components/shapes/Line/LineShape';
import TagShape from '../../components/shapes/Tag/TagShape';
import StarShape from '../../components/shapes/Star/StarShape';
import RingShape from '../../components/shapes/Ring/RingShape';


const CELL_SIZE_REDUCTION_FACTOR = 0.25;

const GridItem = memo(function GridItem({ index, cell, assignAdTemplate, cell_width, cell_height, spanWidth, spanHeight, originalTemplateWidth, originalTemplateHeight, isSelected, onClick, setShapesLayerRef, onDoubleClick, cellConfig, gridCell}) {
  const [borderStyle, setBorderStyle] = useState('1px dashed #ccc'); // New state variable

  useEffect(() => {
    if (cell) {
      setBorderStyle('none');
    } else {
      setBorderStyle('1px dashed #ccc');
    }
  }, [cell]);

  useEffect(() => {
    if (isSelected) {
      setBorderStyle('1px dashed #808080'); // Highlight selected cells
    } else if (cell_width ===0 || cell) {
      setBorderStyle('none');
    } else {
      setBorderStyle('1px dashed #ccc');
    }
  }, [cell, isSelected, cell_width]);

  useEffect(() => {
    if(cell_width === 0) {
      setBorderStyle('none');
    } else if (cell_height === 0) {
      setBorderStyle('none');
    }
  }, [cell_width, cell_height]);
  
  const [{ isOver }, drop] = useDrop({
    accept: 'template',
    drop: (item) => {
      assignAdTemplate(index, item.id);
      //setBorderStyle('none'); // Set isTemplateDropped to true when a template is dropped
    },
    collect: (monitor) => ({
      canDrop: monitor.canDrop(),
      isOver: monitor.isOver(),
    }),
  });

  // Memoize the scaled shapes to avoid recalculating them on every render
  const scaledShapes = useMemo(() => {
    if (!cell) return [];
    return cell.map(shape => scaleShape(shape, cell_width, cell_height));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cell, cell_width, cell_height]);

  function scaleShape(shape, cellWidth, cellHeight) {

    const scale_x = cellWidth / originalTemplateWidth;
    const scale_y = cellHeight / originalTemplateHeight;

    const scaledShape = {...shape};
    // For all shapes
    if (shape.x) scaledShape.x *= scale_x;
    if (shape.y) scaledShape.y *= scale_y;
    
    if (shape.strokeWidth) scaledShape.strokeWidth *= scale_y;
  
    // For shapes with width and height (rectangle, image)
    if (shape.width) scaledShape.width *= scale_x;
    if (shape.height) scaledShape.height *= scale_y;

    // For circular shapes (circle, arc)
    if (shape.radius) scaledShape.radius *= scale_x;

    // For rounded rectangles
    if (shape.cornerRadius) scaledShape.cornerRadius *= scale_x;

    // For arc
    if (shape.innerRadius) scaledShape.innerRadius *= scale_x;
    if (shape.outerRadius) scaledShape.outerRadius *= scale_x;
  
    // For arrow
    if (shape.points) {
      scaledShape.points = shape.points.map(point => point * scale_x);
    }
  
    // For text
    if (shape.fontSize) {
      scaledShape.fontSize *=  scale_x;

    }  

    
    // New scaling properties for text  
    if (!shape.fontSize && shape.lineHeight) scaledShape.lineHeight *= scale_y;
    if (!shape.fontSize && shape.padding) scaledShape.padding *= scale_x;

    scaledShape.draggable = false;
    return scaledShape;
  }

  const cellType = cellConfig?.cellType;
  const cellCategory = cellConfig?.category;

  return (
    <div
      className='grid-item'
      ref={drop}
      style={{ display: cell_width === 0 ? 'none' : 'block', width: cell_width, height: cell_height, gridColumn: 'span ' + spanWidth, gridRow: 'span ' + spanHeight, border: borderStyle, backgroundColor: isOver ? '#f0f0f0' : (gridCell?.background_color || 'white'), margin:'0', position: 'relative' }}
      key={index}
      onContextMenu={(e) => {
        e.preventDefault();
      }}
      onClick={onClick}
      onDoubleClick={(e) => { e.stopPropagation(); onDoubleClick && onDoubleClick(index); }}
    >
      {cellType && cellType !== 'product' && (
        <div className={`cell-type-badge ${cellType}`}>{cellType}</div>
      )}
      {cellCategory && (
        <div className="cell-category-badge">{cellCategory}</div>
      )}
      {scaledShapes && (
        <CellTemplate
          width={cell_width}
          height={cell_height}
          backgroundColor={gridCell?.background_color || 'white'}
          borderColor={gridCell?.border_color}
          borderWidth={gridCell?.border_width || 0}
          borderRadius={gridCell?.border_radius || 0}
          onShapesLayerReady={(shapesLayer) => setShapesLayerRef(shapesLayer)}
        >
          {scaledShapes.map((scaledShape) => {
            switch (scaledShape.type) {
              case 'rectangle':
                return <RectangleShape {...scaledShape} key={scaledShape.id} />;
              case 'circle':
                return <CircleShape {...scaledShape} key={scaledShape.id} />;
              case 'arc':
                return <ArcShape {...scaledShape} key={scaledShape.id} />;
              case 'arrow':
                return <ArrowShape {...scaledShape} key={scaledShape.id} />;
              case 'image':
                return <ImageShape {...scaledShape} key={scaledShape.id} />;
              case 'text':
                return <TextShape {...scaledShape} key={scaledShape.id} />;
              case 'line':
                return <LineShape {...scaledShape} key={scaledShape.id} />;
              case 'tag':
                return <TagShape {...scaledShape} key={scaledShape.id} />;
              case 'star':
                return <StarShape {...scaledShape} key={scaledShape.id} />;
              case 'ring':
                return <RingShape {...scaledShape} key={scaledShape.id} />;
              default:
                return null;
            }
          })}
        </CellTemplate>
      )}
    </div>
  );
});

function FlyerTemplate({ id, grid, setGrid, gridDimensions, selectedCells, setSelectedCells, showCenterGuides = false, onCellDoubleClick, cellConfigs = [] }) {
  const {getAccessTokenSilently} = useAuth0();
  const [shapesLayerRef, setShapesLayerRef] = useState(null);

  const number_of_cells = gridDimensions === '4x4' ? 4 : (gridDimensions === '4x3' ? 3 : null);
  const cell_width = gridDimensions === '4x4' ? 75: 100;
//  const originalTemplateWidth = gridDimensions === '4x4' ? 300: 400; // Original size of the templates
  
  // Function to assign a template to a cell
  const assignAdTemplate = useCallback(async (cellIndex, selectedAdTemplateId) => {
    const adTemplateId = selectedAdTemplateId;

    // Fetch the template from the backend
    const token = await getAccessTokenSilently();
    const response = await axios.get(`${process.env.REACT_APP_SERVER_DOMAIN}/api/templates/${adTemplateId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    // If the template exists
    if (response.data.data) {
      const adTemplateData = JSON.parse(response.data.data);
      const adDimensions = response.data.ad_dimensions;

      // For enhanced templates, merge all layer shapes into content for rendering
      let allShapes = adTemplateData;
      if (response.data.static_shapes || response.data.shared_shapes || response.data.product_block) {
        const staticS = response.data.static_shapes ? JSON.parse(response.data.static_shapes) : [];
        const sharedS = response.data.shared_shapes ? JSON.parse(response.data.shared_shapes) : [];
        const productS = response.data.product_block?.shapes ? JSON.parse(response.data.product_block.shapes) : [];
        allShapes = [...staticS, ...sharedS, ...productS];
      }

      // Update the grid
      setGrid(prevGrid => {
        const newGrid = [...prevGrid];

        newGrid[cellIndex].content = allShapes;

        if(adDimensions==='1x3/4') {
          newGrid[cellIndex].templateWidth=300;
          newGrid[cellIndex].templateHeight=400;
        } else if(adDimensions==='1x3') {
          newGrid[cellIndex].templateWidth=1200;
          newGrid[cellIndex].templateHeight=400;
        } else if(adDimensions==='1x3/2') {
          newGrid[cellIndex].templateWidth=600;
          newGrid[cellIndex].templateHeight=400;
        } else if(adDimensions==='4x1') {
          newGrid[cellIndex].templateWidth=400;
          newGrid[cellIndex].templateHeight=1600;
        }

        // Preserve enhanced template fields
        if (response.data.static_shapes) newGrid[cellIndex].static_shapes = response.data.static_shapes;
        if (response.data.shared_shapes) newGrid[cellIndex].shared_shapes = response.data.shared_shapes;
        if (response.data.product_block) newGrid[cellIndex].product_block = response.data.product_block;
        if (response.data.background_color) newGrid[cellIndex].background_color = response.data.background_color;
        if (response.data.border_color) newGrid[cellIndex].border_color = response.data.border_color;
        if (response.data.border_width) newGrid[cellIndex].border_width = response.data.border_width;
        if (response.data.border_radius) newGrid[cellIndex].border_radius = response.data.border_radius;
        if (response.data.cell_padding) newGrid[cellIndex].cell_padding = response.data.cell_padding;

        // Carry product cell flag (auto-detected during ad template save)
        newGrid[cellIndex].is_product_cell = response.data.is_product_cell !== false;

        return newGrid;
      });
    } else {
      alert(`No template found with ID: ${adTemplateId}`);
    }
  }, [getAccessTokenSilently, setGrid]);

  const toggleCellSelection =  (index) => {
    setSelectedCells(prev => {
      if (prev.includes(index)) {
        return prev.filter(i => i !== index);
      } else {
        return [...prev, index];
      }
    });
  };

 

  const gridWidth = cell_width * number_of_cells;
  const gridHeight = 400; // 4 rows * 100px each

  return (
    <div className="flyer-template-canvas" style={{width: ((cell_width * number_of_cells) + (24*3))}}>
      <div className="flyer-grid" style={{ position: 'relative' }}>
        <div id={id} style={{ display: 'grid', gridTemplateColumns: 'repeat('+ parseInt(gridDimensions.split('x')[1], 10)+', 1fr)', columnGap:'0', rowGap: '0', width: gridWidth }}> {/* Wrapper div for the grid */}
          {Array.isArray(grid) && grid.map((gridCell, index) =>  (
            <GridItem
            key={index}
            index={index}
            cell={gridCell.content}
            assignAdTemplate={assignAdTemplate}
            shapesLayerRef={shapesLayerRef}
            setShapesLayerRef={setShapesLayerRef}
            cell_width={gridCell.dimensions.width * CELL_SIZE_REDUCTION_FACTOR}
            cell_height={gridCell.dimensions.height * CELL_SIZE_REDUCTION_FACTOR}
            spanWidth={gridCell.spanWidth}
            spanHeight={gridCell.spanHeight}
            isSelected={selectedCells.includes(index)}
            onClick={() => {toggleCellSelection(index)}}
            originalTemplateWidth={gridCell.templateWidth}
            originalTemplateHeight={gridCell.templateHeight}
            onDoubleClick={onCellDoubleClick}
            cellConfig={cellConfigs[index]}
            gridCell={gridCell}
            />
          ))}
        </div>
        {/* Center Guidelines */}
        {showCenterGuides && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: gridWidth,
            height: gridHeight,
            pointerEvents: 'none',
            zIndex: 100
          }}>
            <div style={{
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: '1.5px',
              background: 'repeating-linear-gradient(to right, #ff8264 0, #ff8264 6px, transparent 6px, transparent 10px)'
            }} />
            <div style={{
              position: 'absolute',
              left: '50%',
              top: 0,
              bottom: 0,
              width: '1.5px',
              background: 'repeating-linear-gradient(to bottom, #ff8264 0, #ff8264 6px, transparent 6px, transparent 10px)'
            }} />
          </div>
        )}
      </div>

    </div>
  );
}

FlyerTemplate.defaultProps = {
  grid: [],
};

export default FlyerTemplate;